import isNil from 'lodash/isNil';
import last from 'lodash/last';
import round from 'lodash/round';

import {abbreviateNumber, commaizeNumber} from 'common/util';
import {formatDuration, getPrecision} from 'common/util/date';
import {IChartDataItem} from 'common/components/line-chart/types';
import {LineStyle} from 'common/components/line-chart/enums';
import {TooltipBulletStyle, TooltipItemType, TooltipRow, TooltipSection} from 'common/components/chart-tooltip/types';
import {ValueRepresentationType} from 'common/report/constants';
import {BucketData, FunnelSegment, FunnelStep} from 'common/report/models/funnels/types';
import {formatPercent} from 'common/report/util/funnels/utils';
import {ColorChoice} from 'common/report/util/funnels/colors';

import {FunnelStepBucketAccessors} from '../frequency-chart/utils';
import {BarDescriptor, Bucket, BucketDefinition, SelectedBar, SelectedSegmentStep, TextLabelFormatter} from './types';

export const MAX_DOWNLOAD_SEGMENTS = 30;

export function getSelectedCount(bucketData: BucketData) {
  return (bucketData.buckets || []).reduce((a, i) => a + i, 0) + (bucketData.higher || 0) + (bucketData.lower || 0);
}

export function getBucketfromBookmarkFormat(bucket: any): BucketDefinition {
  return {
    min: bucket.min,
    max: bucket.max,
    bucketSize: bucket.bucket_size,
    offset: bucket.offset,
  };
}

/**
 * Returns bucketed data for the appropriate step of the funnel.
 */
export function getBucketData(funnelStep: FunnelStep, bucketAccessor: keyof FunnelStep): BucketData {
  if (!funnelStep || !funnelStep.count) {
    return {
      higher: 0,
      lower: 0,
      buckets: [],
    };
  }
  return (funnelStep[bucketAccessor] as unknown) as BucketData;
}

export function getFormattedUserCount(bucketData: BucketData) {
  return `${commaizeNumber(getSelectedCount(bucketData))}`;
}

export function percentOfTotal(bucketData: BucketData, n: number, showCountAsPercentage = false, precision = 2) {
  const total = !showCountAsPercentage ? getSelectedCount(bucketData) : 1;
  return total ? round((n / total) * 100, precision) : 0;
}

/** Current step of data for a given segment. */
export function getFunnelStep(funnelSegment: FunnelStep[], stepIndex: number = null) {
  return stepIndex === null ? last(funnelSegment) : funnelSegment[stepIndex];
}

export function getMedian(funnelStep: FunnelStep, medianAccesor: keyof FunnelStep) {
  return funnelStep && (funnelStep[medianAccesor] || [])[0];
}

/**
 * To be clickable, a bucket must contain at least 2 users and be greater than
 * or equal to their given minimum
 * @param {number} bucketValue - number of users in a bucket
 * @param {number} bucketSize - size of bucket in seconds
 * @param {number} bucketClickMinimum - minimum bucketSize to allow for popover
 * @return {boolean} whether one can further drilldown on the bucket
 */
function isBucketClickable(bucketValue: number, bucketSize: number, bucketClickMinimum: number) {
  return bucketSize >= bucketClickMinimum && ![0].includes(bucketValue);
}

function getBarChartBucketData({
  segmentData,
  bucketSize,
  selection,
  bucketAccessor,
  medianAccessor,
  showAnnotation,
  bucketStart,
  bucketEnd,
  bucketLabel,
  labelFormatter,
  index,
  bucketClickMinimum,
  showCountAsPercentage,
}: {
  segmentData: SelectedSegmentStep[];
  bucketSize: number;
  selection: SelectedBar;
  bucketAccessor: keyof FunnelStep;
  medianAccessor: keyof FunnelStep;
  showAnnotation: boolean;
  bucketStart: number;
  bucketEnd: number;
  bucketLabel: string;
  labelFormatter: TextLabelFormatter;
  index: number;
  bucketClickMinimum: number;
  showCountAsPercentage: boolean;
}): BarDescriptor[] {
  const bucketDescriptor = [] as BarDescriptor[];
  segmentData.forEach((segment) => {
    // For the selected segment, get the color, average, median, and buckets
    // For each bucket in the segment,
    // Append a segment object to the appropriate row
    const {segmentKey, segmentName, step, color, style} = segment;
    const bucketData = getBucketData(step, bucketAccessor);
    const buckets = bucketData.buckets;
    const median = showAnnotation ? getMedian(step, medianAccessor) : null;
    let count = buckets.length > 0 ? buckets[index] : 0;
    //index=-1 for overall lower and index=buckets.length for overall higher
    if (index < 0 || index >= buckets.length) {
      count = index >= buckets.length ? bucketData.higher : bucketData.lower;
    }
    const containsMedian = !isNil(median) && bucketStart <= median && median < bucketEnd;
    const isSelected = !!selection && selection.segment.label === segmentName && selection.rowLabel === bucketLabel;
    const percentage = percentOfTotal(bucketData, count, showCountAsPercentage);

    bucketDescriptor.push({
      color,
      isClickable:
        index < 0 || index >= buckets.length ? false : isBucketClickable(count, bucketSize, bucketClickMinimum),
      count,
      isSelected,
      label: segmentName,
      name: segmentKey,
      percentage,
      ...(containsMedian ? {annotation: {value: labelFormatter.getMedianFormatter(median)}} : null),
      ...(style ? {style} : null),
    });
  });

  return bucketDescriptor;
}

/**
 * Returns an array of formatted  data for a given funnel step.
 * Buckets that have no data are truncated off the end.
 * @return {Bucket[]} descriptor for each bucket
 */
export function getBarChartData({
  segmentData,
  selectedBucket,
  selection,
  bucketAccessor,
  medianAccessor,
  labelFormatter,
  overallStep,
  showAnnotation = true,
  bucketClickMinimum = 2,
  showCountAsPercentage = false,
}: {
  segmentData: SelectedSegmentStep[];
  selectedBucket: BucketDefinition;
  selection: SelectedBar;
  bucketAccessor: keyof FunnelStep;
  medianAccessor: keyof FunnelStep;
  labelFormatter: TextLabelFormatter;
  overallStep?: FunnelStep;
  showAnnotation?: boolean;
  bucketClickMinimum?: number;
  showCountAsPercentage?: boolean;
}): Bucket[] {
  const barChartData: Bucket[] = [];
  // Calculate bucket properties
  const {bucketSize, min: bucketMin, max: bucketMax} = selectedBucket;
  const rangeStart = bucketMin || 0;
  const numBuckets = Math.ceil((bucketMax - bucketMin) / bucketSize);
  const end = rangeStart + numBuckets * bucketSize;

  const overallBucketData = getBucketData(overallStep, bucketAccessor);
  if (!overallBucketData) {
    return barChartData;
  }

  const {higher: overallHigher, lower: overallLower, buckets: overallBuckets} = overallBucketData;

  // first non-zero index if there is overall lower
  const firstNonZeroIndex = overallBuckets.length ? overallBuckets.findIndex((count) => count > 0) : 0;
  // Get the last non-zero index from the overall segment if there is no overall higher
  const lastNonZeroIndex = overallBuckets.length
    ? overallBuckets.reduce((lastIndex, count, index) => (count > 0 ? index : lastIndex), 0) + 1
    : numBuckets;

  // Generate the "lower" bucket
  if (overallLower) {
    const label = `< ${labelFormatter.getBucketLabel(rangeStart)}`;
    if (showCountAsPercentage) {
      const segments = getBarChartBucketData({
        segmentData,
        bucketSize,
        selection,
        bucketAccessor,
        medianAccessor,
        showAnnotation,
        bucketStart: 0,
        bucketEnd: rangeStart,
        bucketLabel: label,
        labelFormatter,
        index: -1,
        bucketClickMinimum,
        showCountAsPercentage,
      });

      barChartData.push({
        start: 0,
        end: rangeStart,
        label,
        segments,
        queryIndex: -1,
      });
    } else {
      barChartData.push({
        label,
        text: `There are ${commaizeNumber(overallLower)} values (${percentOfTotal(
          overallBucketData,
          overallLower,
        )}% of total) that fall outside the bounds of this chart.`,
      });
    }
  } else if (firstNonZeroIndex >= 0 && overallBuckets[0] === 0) {
    const endNoConversionsTime = rangeStart + firstNonZeroIndex * bucketSize;
    barChartData.push({
      label: labelFormatter.getBucketLabel(0, endNoConversionsTime),
      text: `There were no conversions in this window.`,
    });
  }

  // Creates a skeleton chart if there is no segmentData
  for (let index = firstNonZeroIndex; index < lastNonZeroIndex; index++) {
    const bucketRangeStart = rangeStart + index * bucketSize;
    const bucketRangeEnd = bucketRangeStart + bucketSize;
    const endTime = rangeStart + bucketSize * bucketSize;
    const endUnit = formatDuration(endTime * 1000, 0).unit;
    const precision = getPrecision(endUnit, bucketSize * 1000);
    const bucketLabel = labelFormatter.getBucketLabel(bucketRangeStart, bucketRangeEnd, precision);

    const segments = getBarChartBucketData({
      segmentData,
      bucketSize,
      showAnnotation,
      bucketAccessor,
      medianAccessor,
      selection,
      bucketStart: bucketRangeStart,
      bucketEnd: bucketRangeEnd,
      bucketLabel,
      labelFormatter,
      index,
      bucketClickMinimum,
      showCountAsPercentage,
    });

    barChartData.push({
      start: bucketRangeStart,
      end: bucketRangeEnd,
      label: bucketLabel,
      segments,
      queryIndex: index,
    });
  }

  // No data for the given range
  if (firstNonZeroIndex === -1) {
    barChartData.push({
      label: labelFormatter.getBucketLabel(0, end),
      text: `There were no conversions in this window.`,
    });
  } else if (lastNonZeroIndex < numBuckets) {
    // Generate bucket with 0 values to signal to users that there were no conversions
    // (overall / segmented) beyond the bucket with lastNonZeroIndex.
    const startNoConversionsTime = rangeStart + lastNonZeroIndex * bucketSize;
    barChartData.push({
      label: labelFormatter.getBucketLabel(startNoConversionsTime, end),
      text: `There were no conversions in this window.`,
    });
  }

  if (overallHigher) {
    const label = `> ${labelFormatter.getBucketLabel(end)}`;
    if (showCountAsPercentage) {
      const segments = getBarChartBucketData({
        segmentData,
        bucketSize,
        selection,
        bucketAccessor,
        medianAccessor,
        showAnnotation,
        bucketStart: end,
        bucketEnd: Number.POSITIVE_INFINITY,
        bucketLabel: label,
        labelFormatter,
        index: numBuckets,
        bucketClickMinimum,
        showCountAsPercentage,
      });

      barChartData.push({
        start: end,
        end: Number.POSITIVE_INFINITY,
        label,
        segments,
        queryIndex: numBuckets,
      });
    } else {
      // Generate the "higher" bucket and add the overall segment
      barChartData.push({
        label,
        text: `There are ${overallHigher} values (${percentOfTotal(
          overallBucketData,
          overallHigher,
        )}% of total) that fall outside the bounds of this chart.`,
      });
    }
  }

  return barChartData;
}

export function getToolTip(
  bucketLabel: string,
  segmentKey: string,
  queryIndex: number,
  isAnnotation: boolean,
  labelFormatter: TextLabelFormatter,
  funnelData: Map<string, SelectedSegmentStep>,
  comparisonData: Map<string, SelectedSegmentStep>,
  bucketAccessor: keyof FunnelStep,
  medianAccessor: keyof FunnelStep,
  dateRangeLabels?: string[],
  countBucketAccessor?: FunnelStepBucketAccessors,
  showCountAsPercentage?: boolean,
): TooltipSection[] {
  // This is only used here, didn't want to duplicate logic.
  function getToolTipRows(data: Map<string, SelectedSegmentStep>): TooltipRow[] | null {
    if (!data) {
      return null;
    }
    const funnelStep = data.get(segmentKey).step;
    const bucketData = getBucketData(funnelStep, bucketAccessor);
    const buckets = bucketData.buckets;
    const medianValue = labelFormatter.getMedianFormatter(getMedian(funnelStep, medianAccessor));
    let count = buckets[queryIndex];
    let percentage = count;

    if (countBucketAccessor) {
      const conversionData = getBucketData(funnelStep, countBucketAccessor);
      //queryIndex=-1 for overall lower and queryIndex=buckets.length for overall higher
      if (queryIndex >= buckets.length || queryIndex < 0) {
        count = queryIndex < 0 ? conversionData.lower : conversionData.higher;
        percentage = queryIndex < 0 ? bucketData.lower : bucketData.higher;
      } else {
        count = conversionData.buckets[queryIndex];
      }
    }

    return isAnnotation
      ? labelFormatter.getMedianToolTipText(medianValue)
      : labelFormatter.getToolTipText(
          count,
          percentOfTotal(bucketData, percentage, showCountAsPercentage),
          bucketLabel,
        );
  }

  const sections = getToolTipRows(funnelData);
  const compareSections = getToolTipRows(comparisonData);
  return getToolTipSections(
    funnelData.get(segmentKey).segmentName,
    funnelData.get(segmentKey).color,
    sections,
    compareSections,
    dateRangeLabels,
  );
}

export function getToolTipSections(
  label: string,
  color: string,
  sections: TooltipRow[],
  compareSections?: TooltipRow[],
  dateRanges?: string[],
): TooltipSection[] {
  let toolTip: TooltipSection[];
  const toolTipTitle = {
    rows: [
      {
        items: [
          {
            text: label,
            type: TooltipItemType.Strong,
          },
        ],
      },
    ],
  };
  if (compareSections) {
    toolTip = [
      toolTipTitle,
      {
        bulletStyle: TooltipBulletStyle.Solid,
        bulletColor: color,
        rows: [
          {
            items: [{text: dateRanges[0], type: TooltipItemType.Detail}],
          },
          ...sections,
        ],
      },
      {}, // spacing
      {
        bulletStyle: TooltipBulletStyle.Dash,
        bulletColor: color,
        rows: [
          {
            items: [{text: dateRanges[1], type: TooltipItemType.Detail}],
          },
          ...compareSections,
        ],
      },
    ];
  } else {
    toolTip = [toolTipTitle, {rows: sections}];
  }
  return toolTip;
}

export function truncateBarChartDataForDownload(data: Bucket[]): {data: Bucket[]; isTruncated: boolean} {
  let segmentsRemaining = MAX_DOWNLOAD_SEGMENTS;
  const truncatedBuckets = data.reduce((acc, bucket) => {
    const bucketLength = bucket.segments?.length || 0;
    if (segmentsRemaining === 0) {
      return acc;
    }

    if (segmentsRemaining - bucketLength >= 0) {
      segmentsRemaining -= bucketLength;
      return [...acc, bucket];
    } else {
      const slicedBucket = {...bucket, segments: bucket.segments.slice(0, segmentsRemaining)};
      segmentsRemaining = 0;
      return [...acc, slicedBucket];
    }
  }, []);

  return {data: truncatedBuckets, isTruncated: segmentsRemaining === 0};
}

/**
 * Given FunnelSegments with buckets(IE Frequency and Time to Convert), return an array of IChartDataItem
 * to be used with mp-line-chart
 * @param segmentData Array of FunnelSegments
 * @param stepIndex Index of given step in FunnelSegment
 * @param bucketAccessor key that correlates to which bucket is being accessed in FunnelSegment
 * @param segmentColors Colors used to filter and color lines
 * @param comparisonData Array of Compared Funnel Segments
 */
export function getLineChartDataforBucketReports({
  segmentData,
  stepIndex,
  bucketAccessor,
  segmentColors,
  isComparison = false,
  representationType = ValueRepresentationType.Relative,
  showCountAsPercentage = false,
}: {
  segmentData: FunnelSegment[];
  stepIndex: number;
  bucketAccessor: keyof FunnelStep;
  segmentColors?: ColorChoice[];
  isComparison: boolean;
  representationType: ValueRepresentationType;
  showCountAsPercentage?: boolean;
}): IChartDataItem[] {
  const visibleData: IChartDataItem[] = [];
  const colorMap = new Map<string, string>();
  if (segmentColors) {
    segmentColors.forEach((colorSeg) => colorMap.set(colorSeg.key, colorSeg.color));
  }

  // If there is given segment colors and segmentData is not present in colorMap, don't include the segment
  segmentData.forEach((segment) => {
    if (!segmentColors || colorMap.has(segment.prop)) {
      visibleData.push(
        buildIChartDataItem(
          segment,
          stepIndex,
          bucketAccessor,
          colorMap,
          isComparison ? LineStyle.Comparison : LineStyle.Standard,
          representationType,
          showCountAsPercentage,
        ),
      );
    }
  });

  return visibleData;
}

function buildIChartDataItem(
  segment: FunnelSegment,
  stepIndex: number,
  bucketAccessor: keyof FunnelStep,
  colorMap?: Map<string, string>,
  dashStyle: LineStyle = LineStyle.Standard,
  representationType: ValueRepresentationType = ValueRepresentationType.Relative,
  showCountAsPercentage = false,
): IChartDataItem {
  const data = new Array<[number, number]>();
  const step = getFunnelStep(segment.steps, stepIndex);
  const bucketData = getBucketData(step, bucketAccessor);
  const buckets = bucketData.buckets;
  // Display values based on representationType
  buckets.forEach((value, index) => {
    data.push([
      index,
      representationType === ValueRepresentationType.Absolute
        ? value
        : percentOfTotal(bucketData, value, showCountAsPercentage),
    ]);
  });
  return {
    data,
    name: segment.prop,
    label: segment.propName,
    dashStyle,
    ...(colorMap ? {color: colorMap.get(segment.prop)} : {}),
  };
}

export function formatXAxis(
  value: number,
  bucket: BucketDefinition,
  labelFormatter: (bucketStart: number, bucketEnd: number) => string,
  range = false,
): string {
  const {bucketSize, min} = bucket;
  // X values are maintained as indices
  value = min + value * bucketSize;
  let end = null;
  if (range && bucketSize > 1) {
    end = value + bucketSize;
  }
  return labelFormatter(value, end);
}

export function formatYAxisLabel(value: number, valueRepresentation: ValueRepresentationType) {
  return valueRepresentation === ValueRepresentationType.Absolute
    ? abbreviateNumber(value)
    : (formatPercent(value / 100, 2) as string);
}
